import { ReactNode } from 'react';
import { BiSolidShoppingBags } from 'react-icons/bi';
import { BsShop } from 'react-icons/bs';
import { CiSettings, CiViewList } from 'react-icons/ci';
import { FaRobot, FaUsers, FaVideo } from 'react-icons/fa';
import { FaRegFileLines } from 'react-icons/fa6';
import { GrTextAlignLeft } from 'react-icons/gr';
import { MdOutlineSupportAgent, MdSpaceDashboard } from 'react-icons/md';
import { RiDiscountPercentLine } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';

import { LiveHagglesIcon } from '../ui/svg/NavIcons';
import { checkUserRoleRubyOrSapphireOrDiamond } from '../utils/utils';

import ProductIcon from '@/assets/menu/ProductIcon';
import { FilterPages } from '@/components/utils/enums';
import { useUser } from '@/hooks/useUser';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { AppRoutes } from '@/interfaces/routes/AppRoutes';

export interface INavigationItem {
	label: string;
	value?: string | null;
	path: string | null;
	icon: JSX.Element;
	requiresAdmin: boolean;
	showFilters?: boolean;
}

interface DashboardNavigationListWithFiltersProps {
	filtersContent?: ReactNode;
	selectedFilterItem?: FilterPages | null;
	onItemClick?: (itemLabel: string, path: string | null) => void;
}

const DashboardNavigationListWithFilters = ({
	filtersContent,
	selectedFilterItem,
	onItemClick,
}: DashboardNavigationListWithFiltersProps) => {
	const { user } = useUser();
	const userIsAdmin = user?.roles.includes('admin');
	const userIsDiamondOrRuby =
		user?.roles.includes(UserRole.MerchantDiamond) ||
		user?.roles.includes(UserRole.MerchantRuby);
	const userIsDiamond = user?.roles.includes(UserRole.MerchantDiamond);
	const userIsSapphireOrRubyOrDiamond = checkUserRoleRubyOrSapphireOrDiamond(
		user?.roles ?? [],
	);
	const location = useLocation();
	const navigate = useNavigate();
	const isCurrentRoute = (path: string | null) => {
		if (path === '' && location.pathname === '/') return true;

		if (!path) return false;

		// Require an exact match only for the main dashboard route
		if (path === AppRoutes.UserDashboard) {
			return location.pathname === path;
		}

		// For all other routes
		return location.pathname.startsWith(path);
	};

	const adminMenu: INavigationItem[] = [
		{
			label: FilterPages.HaggleHub,
			icon: <LiveHagglesIcon />,
			path: ``,
			requiresAdmin: false,
			showFilters: true,
		},
		{
			label: 'Merchants',
			icon: <BiSolidShoppingBags size={20} />,
			path: `${AppRoutes.User}/merchants`,
			requiresAdmin: true,
		},
		{
			label: 'Dashboard',
			icon: <MdSpaceDashboard size={20} />,
			path: AppRoutes.UserDashboard,
			requiresAdmin: false,
		},
		{
			label: 'Haggle Events',
			// value optional key is added cause multiple tests were failing, admin-menu-Haggles is expected to be visible
			value: 'Haggles',
			icon: <RiDiscountPercentLine size={20} />,
			path: userIsAdmin
				? `${AppRoutes.User}/haggles`
				: AppRoutes.UserHaggleEvents,
			requiresAdmin: false,
		},
		{
			label: 'Users',
			icon: <FaUsers size={20} />,
			path: userIsAdmin ? AppRoutes.AdminUsers : AppRoutes.UserUsers,
			requiresAdmin: false,
		},
		...(userIsDiamond
			? [
					{
						label: 'Haggle Invited',
						icon: <FaUsers size={20} />,
						path: AppRoutes.HaggleInvitedUsers,
						requiresAdmin: false,
					},
			  ]
			: []),

		{
			label: 'Invited Users',
			icon: <FaUsers size={20} />,
			path: `${AppRoutes.User}/invited`,
			requiresAdmin: true,
		},
		{
			label: 'Prompts',
			icon: <GrTextAlignLeft size={20} />,
			path: `${AppRoutes.User}/prompts`,
			requiresAdmin: true,
		},
		{
			label: 'Products',
			icon: <ProductIcon />,
			path: userIsAdmin ? AppRoutes.AdminProducts : AppRoutes.UserProducts,
			requiresAdmin: false,
		},
		{
			label: 'Reports',
			icon: <FaRegFileLines size={20} />,
			path: userIsAdmin ? AppRoutes.AdminReports : AppRoutes.UserReports,
			requiresAdmin: false,
		},
		{
			label: 'Orders',
			icon: <CiViewList size={20} />,
			path: AppRoutes.Orders,
			requiresAdmin: false,
		},
		...(userIsSapphireOrRubyOrDiamond
			? [
					{
						label: 'Customization',
						icon: <FaRobot size={20} />,
						path: `${AppRoutes.User}/customize-chatbot`,
						requiresAdmin: false,
					},
			  ]
			: []),
		...(userIsDiamondOrRuby
			? [
					{
						label: 'B2B Marketplace',
						icon: <BsShop size={20} />,
						path: `${AppRoutes.User}/b2b-vendors`,
						requiresAdmin: false,
					},
			  ]
			: []),
		{
			label: 'Settings',
			icon: <CiSettings size={20} />,
			path: `${AppRoutes.User}/settings`,
			requiresAdmin: false,
		},
		{
			label: 'How-to Videos',
			icon: <FaVideo size={20} />,
			path: AppRoutes.HowToVideos,
			requiresAdmin: false,
		},
		{
			label: 'Support',
			icon: <MdOutlineSupportAgent size={20} />,
			path: `${AppRoutes.User}/support`,
			requiresAdmin: false,
		},
	];

	const filteredMenu = adminMenu.filter((item) => {
		if (userIsAdmin && item.label === 'Dashboard') {
			return false;
		}
		return !item.requiresAdmin || userIsAdmin;
	});

	const handleItemClick = (item: INavigationItem) => {
		// Call the parent's onItemClick handler
		onItemClick?.(item.label, item.path);

		// Navigate to the route
		if (item.path === '') {
			navigate('/');
		} else {
			const targetPath = item.path?.startsWith('/user')
				? item.path
				: `/user/${item.path?.replace(/^\/+/, '')}`;
			navigate(targetPath);
		}
	};

	return (
		<div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-4 py-3.5">
			<div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 gap-4">
				{filteredMenu.map((item) => {
					const { label: name, icon, path, value, showFilters } = item;
					const isSelected = isCurrentRoute(path);
					const shouldShowFilters =
						showFilters && selectedFilterItem === name && filtersContent;

					return (
						<div key={name} className="w-full">
							<button
								data-test={`admin-menu-${value ? value : name}`}
								className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-gray-100
								${isSelected && 'bg-grey-10 border-l-4 border-purpleBrand-500 font-semibold'}`}
								onClick={() => handleItemClick(item)}
							>
								{icon}
								<span
									className={`text-md ${isSelected && 'text-purpleBrand-500'}`}
								>
									{name}
								</span>
							</button>

							{/* Render filters below the selected item if applicable */}
							{shouldShowFilters && (
								<div className="w-full">{filtersContent}</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default DashboardNavigationListWithFilters;
